import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MainLayout } from "@/components/layout/MainLayout";
import { motion } from "framer-motion";
import { Shield, Smartphone, Check, Copy, AlertTriangle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";

export default function TwoFactorSetupPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const [step, setStep] = useState<"intro" | "setup" | "verify" | "complete">("intro");
  const [qrCode, setQrCode] = useState<string>("");
  const [secret, setSecret] = useState<string>("");
  const [factorId, setFactorId] = useState<string>("");
  const [verifyCode, setVerifyCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [mfaEnabled, setMfaEnabled] = useState(false);

  useEffect(() => {
    if (user) {
      checkMfaStatus();
    }
  }, [user]);

  const checkMfaStatus = async () => {
    try {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error) throw error;
      
      const verifiedFactor = data.totp.find(f => f.status === "verified");
      setMfaEnabled(!!verifiedFactor);
      if (verifiedFactor) {
        setStep("complete");
      }
    } catch (error) {
      console.error("Error checking MFA status:", error);
    }
  };

  const startSetup = async () => {
    setLoading(true);
    try {
      const { data: existingFactors } = await supabase.auth.mfa.listFactors();
      if (existingFactors?.totp) {
        for (const factor of existingFactors.totp) {
          if ((factor.status as string) !== "verified") {
            await supabase.auth.mfa.unenroll({ factorId: factor.id });
          }
        }
      }

      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: "totp",
        friendlyName: "Authenticator App",
      });
      
      if (error) throw error;
      
      setQrCode(data.totp.qr_code);
      setSecret(data.totp.secret);
      setFactorId(data.id);
      setStep("setup");
    } catch (error: any) {
      toast({
        title: "שגיאה",
        description: "לא ניתן להפעיל אימות דו-שלבי",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const verifySetup = async () => {
    if (verifyCode.length !== 6) {
      toast({
        title: "שגיאה",
        description: "קוד האימות חייב להכיל 6 ספרות",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.auth.mfa.challengeAndVerify({
        factorId,
        code: verifyCode,
      });
      
      if (error) throw error;
      
      setMfaEnabled(true);
      setStep("complete");
      toast({
        title: "הצלחה",
        description: "אימות דו-שלבי הופעל בהצלחה",
      });
    } catch (error: any) {
      toast({
        title: "שגיאה",
        description: "קוד האימות שגוי, נסה שוב",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const disableMfa = async () => {
    setLoading(true);
    try {
      const { data } = await supabase.auth.mfa.listFactors();
      const factor = data?.totp.find(f => f.status === "verified");
      
      if (factor) {
        const { error } = await supabase.auth.mfa.unenroll({ factorId: factor.id });
        if (error) throw error;
      }
      
      setMfaEnabled(false);
      setStep("intro");
      toast({
        title: "הצלחה",
        description: "אימות דו-שלבי בוטל",
      });
    } catch (error: any) {
      toast({
        title: "שגיאה",
        description: "לא ניתן לבטל אימות דו-שלבי",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const copySecret = () => {
    navigator.clipboard.writeText(secret);
    toast({
      title: "הועתק",
      description: "הקוד הסודי הועתק ללוח",
    });
  };

  return (
    <MainLayout>
      <div className="flex-1 flex flex-col bg-background p-6" dir="rtl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-4 mb-8"
        >
          <Button
            variant="ghost"
            size="lg"
            onClick={() => navigate("/settings")}
            className="p-3 rounded-2xl"
            data-scan-item
          >
            <ArrowRight className="w-8 h-8" />
          </Button>
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-primary" />
            <h1 className="text-3xl font-bold text-foreground">אימות דו-שלבי (2FA)</h1>
          </div>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="max-w-lg mx-auto w-full"
        >
          <div className="bg-card rounded-3xl border-2 border-border p-8 space-y-6">
            <p className="text-muted-foreground text-center">
              הוסף שכבת אבטחה נוספת לחשבון שלך
            </p>

            {step === "intro" && !mfaEnabled && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-6"
              >
                <div className="flex items-start gap-4 p-5 rounded-xl bg-muted">
                  <Smartphone className="w-8 h-8 text-primary mt-1" />
                  <div>
                    <h4 className="font-medium text-lg">איך זה עובד?</h4>
                    <p className="text-muted-foreground mt-2">
                      תצטרך להתקין אפליקציית אימות כמו Google Authenticator או Authy,
                      ולסרוק קוד QR כדי להוסיף את החשבון.
                    </p>
                  </div>
                </div>

                <Button
                  onClick={startSetup}
                  disabled={loading}
                  size="lg"
                  className="w-full h-16 text-lg rounded-2xl"
                  data-scan-item
                >
                  {loading ? "מפעיל..." : "התחל הגדרה"}
                </Button>
              </motion.div>
            )}

            {step === "setup" && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-6"
              >
                <div className="text-center">
                  <p className="text-muted-foreground mb-4">
                    סרוק את קוד ה-QR באפליקציית האימות שלך
                  </p>
                  <div className="bg-white p-4 rounded-xl inline-block">
                    <img src={qrCode} alt="QR Code" className="w-48 h-48" />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>או הזן את הקוד הסודי ידנית:</Label>
                  <div className="flex gap-2">
                    <Input
                      value={secret}
                      readOnly
                      className="font-mono text-xs h-12"
                      dir="ltr"
                    />
                    <Button variant="outline" size="icon" onClick={copySecret} className="h-12 w-12" data-scan-item>
                      <Copy className="w-5 h-5" />
                    </Button>
                  </div>
                </div>

                <Button onClick={() => setStep("verify")} size="lg" className="w-full h-14 text-lg rounded-2xl" data-scan-item>
                  המשך
                </Button>
              </motion.div>
            )}

            {step === "verify" && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-6"
              >
                <div className="space-y-3">
                  <Label className="text-lg">הזן את הקוד מאפליקציית האימות:</Label>
                  <Input
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="000000"
                    className="text-center text-3xl tracking-widest font-mono h-16"
                    dir="ltr"
                    maxLength={6}
                  />
                </div>

                <Button
                  onClick={verifySetup}
                  disabled={loading || verifyCode.length !== 6}
                  size="lg"
                  className="w-full h-14 text-lg rounded-2xl"
                  data-scan-item
                >
                  {loading ? "מאמת..." : "אמת והפעל"}
                </Button>

                <Button
                  variant="ghost"
                  onClick={() => setStep("setup")}
                  size="lg"
                  className="w-full h-12 text-lg"
                  data-scan-item
                >
                  חזרה
                </Button>
              </motion.div>
            )}

            {step === "complete" && mfaEnabled && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-6"
              >
                <div className="flex items-center gap-4 p-5 rounded-xl bg-green-500/10 text-green-600">
                  <Check className="w-8 h-8" />
                  <div>
                    <h4 className="font-medium text-lg">אימות דו-שלבי מופעל</h4>
                    <p className="opacity-80">
                      החשבון שלך מוגן באימות דו-שלבי
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-5 rounded-xl bg-amber-500/10 text-amber-600">
                  <AlertTriangle className="w-6 h-6 mt-0.5" />
                  <p>
                    אם תבטל את האימות הדו-שלבי, החשבון יהיה פחות מאובטח.
                  </p>
                </div>

                <Button
                  variant="destructive"
                  onClick={disableMfa}
                  disabled={loading}
                  size="lg"
                  className="w-full h-14 text-lg rounded-2xl"
                  data-scan-item
                >
                  {loading ? "מבטל..." : "בטל אימות דו-שלבי"}
                </Button>
              </motion.div>
            )}
          </div>
        </motion.div>
      </div>
    </MainLayout>
  );
}
